import { useTheme } from "@emotion/react";
import {
  ExpandMore,
  FilterAltOutlined,
  PlayArrowOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import useStyles from "./styles";

const EXEMPLO = "AND mfa.descricao LIKE '%B2B%'";

const mensagemErro = (error, padrao) =>
  error?.response?.data?.error || error?.response?.data?.message || padrao;

export default function ConfigMotivoFechamento() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Configuração de cada ERP (HUBSOFT A e B têm a sua): a tela mostra e salva
  // a do ERP escolhido.
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado([
    "HUBSOFT",
  ]);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;
  // Busca só depois da primeira abertura do accordion (e de novo ao trocar de ERP).
  const [jaAberto, setJaAberto] = useState(false);

  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [config, setConfig] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  // Resultado de "Testar filtro" para o texto atual (null = sem prévia).
  const [previa, setPrevia] = useState(null);
  const [erroFiltro, setErroFiltro] = useState("");

  const aplicar = (dados) => {
    setConfig(dados);
    setFiltro(dados?.filtroSql || "");
    setPrevia(null);
    setErroFiltro("");
  };

  const getConfig = async () => {
    const erpPedido = erpId;
    setLoading(true);
    try {
      const response = await api.post("/interno/find/configMotivoFechamento", {
        erpId: erpPedido,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      aplicar(response?.data);
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: mensagemErro(
          error,
          "Erro ao buscar a configuração de motivo de fechamento!",
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carregado || !jaAberto) return;
    setIsEdit(false);
    getConfig();
  }, [carregado, erpId, jaAberto]);

  const handleEdit = () => setIsEdit(true);

  const handleCancel = () => {
    setIsEdit(false);
    aplicar(config);
  };

  const handleChange = (valor) => {
    setFiltro(valor);
    setPrevia(null);
    setErroFiltro("");
  };

  const testar = async () => {
    setTestando(true);
    setErroFiltro("");
    try {
      const response = await api.post(
        "/interno/preview/configMotivoFechamento",
        { erpId: config?.erp?.id || erpId, filtroSql: filtro },
      );
      setPrevia(response?.data?.motivos || []);
    } catch (error) {
      console.log(error);
      setPrevia(null);
      setErroFiltro(mensagemErro(error, "Não foi possível testar o filtro."));
    } finally {
      setTestando(false);
    }
  };

  const salvar = async () => {
    setSalvando(true);
    setErroFiltro("");
    try {
      const response = await api.post(
        "/interno/update/configMotivoFechamento",
        {
          erpId: config?.erp?.id || erpId,
          filtroSql: filtro,
        },
      );
      aplicar(response?.data);
      setIsEdit(false);
      setSnackbar({
        children: filtro.trim()
          ? "Filtro de motivo de fechamento atualizado com sucesso!"
          : "Filtro removido. Todos os motivos de fechamento serão exibidos.",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setErroFiltro(mensagemErro(error, "Erro ao salvar o filtro!"));
    } finally {
      setSalvando(false);
    }
  };

  const alterado = filtro.trim() !== (config?.filtroSql || "");
  const motivos = previa ?? config?.motivos ?? [];

  return (
    <>
      <Accordion
        data-secao="motivo"
        onChange={(_, aberto) => aberto && setJaAberto(true)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneOutlined color="primary" />
              <Typography variant="h5" sx={{ ...styles.title }}>
                Configurações de motivo de fechamento
              </Typography>
              {/* Para qual ERP é esta configuração. */}
              <ErpChip
                erp={
                  config?.erp ||
                  erps?.find((item) => item.id === erpId) || { tipo: "HUBSOFT" }
                }
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              label="HubSoft"
              id="filtro-erp-motivo-fechamento"
              sx={{ minWidth: 260, maxWidth: 360, mb: 1.5 }}
            />
          )}
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={styles.loading}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box>
                  <Box sx={styles.headerBox}>
                    <FilterAltOutlined color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Filtro dos motivos de fechamento da HubSoft
                    </Typography>
                  </Box>
                  <Typography sx={styles.subtitle}>
                    Condições SQL adicionadas à lista de motivos de fechamento
                    exibida no encerramento de tickets. Os motivos excluídos ou
                    sem descrição nunca são listados. Sem filtro, todos os
                    demais motivos aparecem.
                  </Typography>
                </Box>

                {!config?.erp && (
                  <Alert severity="warning" sx={styles.alert}>
                    Para salvar, cadastre o ERP HubSoft em Administração › Geral
                    › Configuração interna.
                  </Alert>
                )}

                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  variant="filled"
                  label="Filtro SQL"
                  data-busca="motivo-filtro"
                  placeholder={isEdit ? EXEMPLO : "Sem filtro"}
                  InputLabelProps={{ shrink: true }}
                  value={filtro}
                  onChange={(e) => handleChange(e.target.value)}
                  disabled={!isEdit || salvando}
                  error={!!erroFiltro}
                  helperText={
                    <>
                      Comece com <b>AND</b> e use colunas de <b>mfa</b> (ex.:{" "}
                      <code>{EXEMPLO}</code>). Permitidos: AND, OR, NOT, LIKE,
                      ILIKE, IN, IS NULL, BETWEEN, =, &lt;&gt;, LOWER(),
                      UPPER(). Comandos como SELECT, UPDATE ou DELETE são
                      bloqueados. Deixe vazio para não filtrar.
                    </>
                  }
                  sx={styles.campoSql}
                />

                {erroFiltro && (
                  <Alert severity="error" sx={styles.alert}>
                    {erroFiltro}
                  </Alert>
                )}

                <Box>
                  <Box sx={styles.motivosHeader}>
                    <Typography sx={styles.atualLabel}>
                      {previa
                        ? "Prévia do filtro"
                        : "Motivos exibidos atualmente"}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={previa ? "info" : "default"}
                      label={`${motivos.length} motivo${motivos.length === 1 ? "" : "s"}`}
                    />
                  </Box>
                  <Box sx={styles.motivosLista}>
                    {motivos.length ? (
                      motivos.map((m) => (
                        <Chip
                          key={m.id}
                          size="small"
                          label={`${m.id} - ${m.descricao}`}
                        />
                      ))
                    ) : (
                      <Typography sx={styles.subtitle}>
                        Nenhum motivo de fechamento encontrado.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleEdit}
                  disabled={!config?.erp}
                >
                  Editar
                </Button>
              ) : (
                <Box sx={styles.boxEditActions}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                    disabled={salvando}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={testar}
                    disabled={testando || salvando}
                    startIcon={
                      testando ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <PlayArrowOutlined />
                      )
                    }
                  >
                    Testar filtro
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={salvar}
                    disabled={!alterado || salvando || testando}
                    startIcon={
                      salvando && <CircularProgress size={14} color="inherit" />
                    }
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
